/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getDiasFestivosDelAñoPag,
  getDiasFestivosDelAño,
  postDiaFestivoDelAño,
  deleteDiaFestivoDelAño,
  patchDiaFestivoDelAño,
  getDiaFestivoDelAño,
  autorizarDiaFestivoDelAño,
} from 'api/configuraciones/clock/DiasFestivos/FestivosDelAño';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import moment from 'moment';
import { render } from '@testing-library/react';

const DiasFestivosDelAñoReloj = (
  {
    permission,
    idDelAño,
    año,
    disableFieldsParent,
  },
) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [pageActual, setPageActual] = useState(1);
  const [disableFields] = useState(disableFieldsParent);

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  const formInstances = [
    form,
  ];

  const fetchPaginated = async (page = 1, q = null) => {
    try {
      setLoading(true);
      const cleanPage = Number(String(page).replace(/\D/g, ''));
      const _data = await getDiasFestivosDelAñoPag({
        page: cleanPage,
        ...q,
        dia_festivo: idDelAño,
      });

      const { results } = _data;

      setSelectedRowKeys([]);
      setPageActual(cleanPage);
      setData({ ..._data, results: [...results] });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  const fetchData = async (page = 1, q = {}) => {
    setLoading(true);
    const _data = await getDiasFestivosDelAñoPag({
      page,
      ...q,
      dia_festivo: idDelAño,
    });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getDiasFestivosDelAño({ dia_festivo: idDelAño }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data?.results?.find((item) => item.id === key);
    const _diasFestivos = await getDiaFestivoDelAño(match.id);
    const formValues = {
      ...formatReceived(_diasFestivos),
      fecha: _diasFestivos.fecha ? moment(_diasFestivos.fecha) : null,
      mes: _diasFestivos.mes?.nombre || '',
      dia_de_la_semana: _diasFestivos.dia_de_la_semana?.nombre || '',
    };
    setTimeout(() => {
      if (_diasFestivos) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(formValues));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    // se pone el valor default
    const defaultDate = moment().year(año);
    form.setFieldsValue({
      fecha: defaultDate,
      mes: capitalize(defaultDate.format('MMMM')),
      dia_de_la_semana: capitalize(defaultDate.format('dddd')),
    });
    form.setFieldValue('mes', capitalize(defaultDate.format('MMMM')));
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        // no existe campo de dia festivo, por eso lo agregue.
        ...formatReceived(form.getFieldsValue()),
        dia_festivo: idDelAño,
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len

      if (values.fecha?.format) {
        values.fecha = values.fecha.format('YYYY-MM-DD');
      }

      const saved = await (key ? patchDiaFestivoDelAño(key, values) : postDiaFestivoDelAño(values));
      if (saved) {
        if (_continue) {
          await fetchData(pageActual);
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData(pageActual);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteDiaFestivoDelAño(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData(pageActual);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });

    const dataForForm = {
      ...record,
      fecha: record.fecha
        ? moment(record.fecha)
        : null,

    };

    form.setFieldsValue(dataForForm);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Día Festivo ya autorizado');
        return;
      }
      const saved = await autorizarDiaFestivoDelAño(key);
      if (saved) {
        const _data = await getDiasFestivosDelAño({ dia_festivo: idDelAño });
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Mes',
      dataIndex: 'mes',
      key: 'mes',
      width: 150,
      render: (_, record) => record.mes?.nombre,
    },
    {
      titleText: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 200,
    },
    {
      titleText: 'Día',
      dataIndex: 'dia_de_la_semana',
      key: 'dia_de_la_semana',
      width: 150,
      render: (_, record) => record.dia_de_la_semana?.nombre,
    },
    {
      titleText: 'Festividad',
      dataIndex: 'festividad',
      key: 'festividad',
      width: 400,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data?.results || []}
          pagination={false}
          paginationCallback={fetchPaginated}
          count={data?.count || 0}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          currentPage={pageActual}
          disableDelete={disableFields}
          disableEdit={disableFields}
          disableAdd={disableFields}
          permission={permission}
          allowExportParam={false}
          allowSearch={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Días Festivos Del año`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha"
                rules={rules.required}
                label="Fecha"
              >
                <DatePicker
                  allowClear
                  format="YYYY-MM-DD"
                  disabled={disableFields}
                  onChange={(value) => {
                    const dia_de_la_semana = value.format('dddd');
                    const mes = value.format('MMMM');
                    form.setFieldValue('dia_de_la_semana', capitalize(dia_de_la_semana));
                    form.setFieldValue('mes', capitalize(mes));
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="mes"
                label="Mes"
              >
                <Input
                  allowClear
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="dia_de_la_semana"
                label="Día"
              >
                <Input
                  allowClear
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="festividad"
                rules={rules.required}
                label="Festividad"
              >
                <Input
                  allowClear
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Día Festivo ${selectedData?.festividad}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el Día Festivo ${selectedData?.festividad}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar el Día Festivo ${selectedData?.festividad}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default DiasFestivosDelAñoReloj;
